import { useEffect, useRef, useState } from 'react'
import { Dialog } from '../Dialog'
import { useHubLang, dateLocaleFor } from '../../lib/i18n'
import { SHOP } from '../../lib/hubCopy'
import { collectUrl, groupCode, loadMyOrders, type MyOrder } from '../../lib/shop'

/**
 * "Pickup with code" — the buyer's half of REC-49.
 *
 * ── A FULL SHEET, NOT A ROW (David, 27 Sep) ─────────────────────────────────
 *
 * It gets scanned in a store room, often dim, by a phone held at arm's length.
 * A big code on a white quiet zone scans; a thumbnail in a list row does not.
 * The white is baked into the image (qrcode's `light`), so a dark surface
 * behind it cannot eat the quiet zone.
 *
 * ── THE CODE COMES FROM THE SERVER, THE QR IS DRAWN HERE ────────────────────
 *
 * `my_orders()` hands out the code only while it is live, and this is only
 * opened when it did. The QR is drawn in the browser from `collectUrl` — no
 * QR service, nothing fetched at runtime (OPS-16), and the code never leaves
 * the device except as the picture on the screen. The library is the one
 * Profile already loads for the business card, imported the same way.
 *
 * ── IT WATCHES FOR THE SCAN ─────────────────────────────────────────────────
 *
 * While open it re-reads the order list every few seconds, so the buyer sees
 * "Collected" the moment the other phone confirms — the handover then ends on
 * the buyer's screen as well as the keeper's, and nobody stands there asking
 * "did it work?". Polling rather than realtime: it runs only while this sheet
 * is open, for a minute or two, and adds no channel or policy to reason about.
 * It stops on any terminal state.
 */
const POLL_MS = 4000

export function PickupSheet({
  order,
  onClose,
  onChanged,
}: {
  /** Null = closed. */
  order: MyOrder | null
  onClose: () => void
  /** The list and the balance behind the sheet re-read when the order moves. */
  onChanged: () => void | Promise<void>
}) {
  const lang = useHubLang()
  const t = SHOP[lang]
  const [qr, setQr] = useState<string | null>(null)
  const [qrFailed, setQrFailed] = useState(false)
  const [outcome, setOutcome] = useState<'live' | 'collected' | 'gone'>('live')

  // A ref, so a parent that passes a fresh function every render does not reset
  // the interval every render — which would be a poll that never fires.
  const changed = useRef(onChanged)
  changed.current = onChanged

  const code = order?.handoverCode ?? null
  const orderId = order?.orderId ?? null

  useEffect(() => {
    setQr(null)
    setQrFailed(false)
    setOutcome('live')
    if (!code) return
    let live = true
    // `(m.default ?? m)` — qrcode ships CommonJS; see Profile.tsx.
    import('qrcode')
      .then((m) => ((m as unknown as { default?: typeof m }).default ?? m))
      .then((qrcode) =>
        qrcode.toDataURL(collectUrl(code), {
          margin: 3,
          width: 720,
          errorCorrectionLevel: 'M',
          color: { dark: '#000000', light: '#ffffff' },
        }),
      )
      .then((url: string) => live && setQr(url))
      .catch(() => live && setQrFailed(true))
    return () => { live = false }
  }, [code])

  useEffect(() => {
    if (!orderId || outcome !== 'live') return
    let live = true
    const tick = async () => {
      try {
        const rows = await loadMyOrders()
        if (!live) return
        const row = rows.find((r) => r.orderId === orderId)
        if (row?.status === 'collected') {
          setOutcome('collected')
          await changed.current()
        } else if (!row || !row.handoverCode) {
          // Cancelled, released on expiry, or the code otherwise withdrawn.
          setOutcome('gone')
          await changed.current()
        }
      } catch {
        // A missed poll is not a state. The next one will say.
      }
    }
    const id = window.setInterval(tick, POLL_MS)
    return () => { live = false; window.clearInterval(id) }
  }, [orderId, outcome])

  if (!order) return null

  const until = order.handoverExpiresAt
    ? new Date(order.handoverExpiresAt).toLocaleDateString(dateLocaleFor(lang), {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null

  return (
    <Dialog open title={t.pickupTitle} onClose={onClose} className="pickup">
      <PickupBody order={order} qr={qr} qrFailed={qrFailed} outcome={outcome} until={until} />
    </Dialog>
  )
}

/** Pure, so the suite can render it. */
export function PickupBody({
  order,
  qr,
  qrFailed,
  outcome,
  until,
}: {
  order: MyOrder
  qr: string | null
  qrFailed: boolean
  outcome: 'live' | 'collected' | 'gone'
  until: string | null
}) {
  const t = SHOP[useHubLang()]
  return (
    <div className="pickup__body">
      <div className="pickup__what">
        <p className="pickup__item">
          {order.itemName}
          {order.variantLabel && order.variantLabel !== 'Standard' && (
            <span className="shop__ordervariant"> · {order.variantLabel}</span>
          )}
        </p>
        <p className="pickup__where">{t.pickupAt(order.roomName, order.roomCity)}</p>
      </div>

      {outcome === 'collected' ? (
        <p className="pickup__done" role="status">
          <span aria-hidden="true">✓ </span>{t.pickupDone}
        </p>
      ) : outcome === 'gone' || !order.handoverCode ? (
        <p className="pickup__gone" role="status">{t.pickupGone}</p>
      ) : (
        <>
          <div className="pickup__qr">
            {qr ? (
              <img src={qr} alt={t.pickupQrAlt(order.itemName)} width={320} height={320} />
            ) : qrFailed ? (
              <p className="composer__error" role="alert">{t.pickupQrFailed}</p>
            ) : (
              <span className="pickup__qrwait" aria-busy="true" />
            )}
          </div>
          <p className="pickup__code">
            <span className="pickup__codelabel">{t.pickupCode}</span>{' '}
            <span className="mono">{groupCode(order.handoverCode)}</span>
          </p>
          {until && <p className="pickup__until">{t.pickupValidUntil(until)}</p>}
          <p className="pickup__how">{t.pickupHow}</p>
          <p className="pickup__private">{t.pickupPrivate}</p>
        </>
      )}
    </div>
  )
}
